import React from 'react';
import { Link } from 'react-router-dom';

import { StatusBadge } from './jobs';
import { STATUS_LABEL } from './copy';
import { StatusIcon } from './report/Visuals';
import { ExampleWorkbookLink } from './exampleFiles';

const FAQ = [
  {
    q: 'Which file do I need?',
    a: (
      <>
        One Olink Explore HT workbook saved as <code>.xlsx</code>. It needs a sheet named <code>Data</code> with
        exactly two rows: a header row that starts with <code>DAid</code>, <code>barcode</code>, <code>Cohort</code> and
        {' '}<code>Diagnose</code>, followed by one column per protein named by its gene, and one row with the
        sample&apos;s values. Numbers stored as text are fine, and empty cells count as not measured.
      </>
    ),
  },
  {
    q: 'Do I have to fill in DAid, barcode, Cohort and Diagnose?',
    a: 'The four columns must be there, in that order, but ProteoRisk does not use what is in them. You can leave them empty.',
  },
  {
    q: 'Why do you ask for age, sex and BMI?',
    a: 'Protein levels change with age, sex and body weight. ProteoRisk adjusts every protein for them before it compares patterns, so the results fit the person the sample came from. BMI is optional: if you leave it empty, the analysis runs without it.',
  },
  {
    q: 'How long does an analysis take?',
    a: 'The scoring itself takes seconds. Most analyses are ready within a few minutes, depending on how busy the queue is. You can close the page and come back later.',
  },
  {
    q: 'What does a score mean?',
    a: 'Each score, from 0 to 100, shows how closely the protein pattern resembles that of people with a condition. Scores are grouped as low, moderate or high. A score is not a diagnosis, and resembling a pattern is not the same as having the condition.',
  },
  {
    q: 'What is the difference between current findings and 10-year risk?',
    a: 'Current findings compare your pattern with people who had a condition when their blood was taken. 10-year risk compares it with people who developed a condition within the following ten years.',
  },
  {
    q: 'Who can see my analyses?',
    a: 'Uploads are processed without any patient record. Your analyses are linked to an anonymous ID kept in this browser, so they appear only here. You can see the ID under Settings.',
  },
];

export default function Help() {
  return (
    <div className="PROTEORISK-docs-container">
      <div className="PROTEORISK-docs-content">
        <div className="PROTEORISK-docs-header">
          <h1>Help</h1>
          <p>Everything you need to run ProteoRisk and read your results</p>
        </div>

        <div className="PROTEORISK-docs-section">
          <div className="PROTEORISK-docs-section-header">
            <i className="fas fa-rocket" aria-hidden="true" />
            <h2>Getting started</h2>
          </div>
          <div className="PROTEORISK-docs-steps">
            {[
              ['Prepare the workbook', 'Export one sample from Olink Explore HT as an .xlsx workbook with a Data sheet, as described below.'],
              ['Fill in the form', 'On the Home page, drop the workbook in and enter the age in whole years and the sex. Add BMI if you know it. The form checks the workbook before anything is uploaded.'],
              ['Wait for the scoring', 'Your analysis appears in Your analyses. Its page shows where it is: received, waiting in queue, scoring, and ready.'],
              ['Read your results', 'Open a ready analysis to see the body systems wheel, your highlights, every condition by body system and the proteins behind each notable finding.'],
            ].map(([title, text], index) => (
              <div key={title} className="PROTEORISK-docs-step">
                <div className="PROTEORISK-docs-step-num">{index + 1}</div>
                <div className="PROTEORISK-docs-step-body">
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="PROTEORISK-docs-section">
          <div className="PROTEORISK-docs-section-header">
            <i className="fas fa-file-excel" aria-hidden="true" />
            <h2>Workbook format</h2>
          </div>
          <p className="PROTEORISK-docs-text">
            ProteoRisk reads the sheet named <code>Data</code>. It must hold exactly one header row and one sample row:
          </p>
          <div className="PROTEORISK-docs-table" role="table" aria-label="Example Data sheet">
            <div role="row" className="head">
              {['DAid', 'barcode', 'Cohort', 'Diagnose', 'ACE', 'PLA2G7', 'REN', '...'].map((cell) => (
                <span key={cell} role="columnheader">{cell}</span>
              ))}
            </div>
            <div role="row">
              {['', '', '', '', '2.31', '-0.84', '1.07', '...'].map((cell, index) => (
                <span key={`cell-${index}`} role="cell">{cell}</span>
              ))}
            </div>
          </div>
          <p className="PROTEORISK-docs-text">
            <ExampleWorkbookLink>Download the example workbook</ExampleWorkbookLink>
            {' '}to see the exact layout. Its values are made up; with age 58, sex Male and BMI 29.4 it gives the
            example report.
          </p>
          <p className="PROTEORISK-docs-text">
            Protein columns are matched to the model by their exact gene name. An Explore HT workbook covers up to
            2,765 of the 2,919 proteins the model was built on; the rest count as not measured.
          </p>
        </div>

        <div className="PROTEORISK-docs-section">
          <div className="PROTEORISK-docs-section-header">
            <i className="fas fa-chart-pie" aria-hidden="true" />
            <h2>Reading your results</h2>
          </div>
          <div className="PROTEORISK-docs-status-list">
            {['typical', 'moderate', 'high'].map((status) => (
              <div key={status} className="PROTEORISK-docs-status-item">
                <span className={`PROTEORISK-system-status ${status}`}>
                  <StatusIcon status={status} size={12} />
                  {STATUS_LABEL[status]}
                </span>
                <span>
                  {status === 'typical' && 'Your proteins look typical for every condition in this body system.'}
                  {status === 'moderate' && 'At least one condition in this body system has a moderate result.'}
                  {status === 'high' && 'At least one condition in this body system has a high result.'}
                </span>
              </div>
            ))}
          </div>
          <p className="PROTEORISK-docs-text">
            For a moderate or high result, the report lists the proteins that weigh most for that condition, when at
            least one of them is outside its typical range. Each protein is shown as a z-score: how far your level is
            from the average, in standard deviations. The shaded band is the typical range, from -1.5 to 1.5, and
            proteins outside it are highlighted. These proteins do not fully explain a result, and they do not show
            what causes it.
          </p>
        </div>

        <div className="PROTEORISK-docs-section">
          <div className="PROTEORISK-docs-section-header">
            <i className="fas fa-tasks" aria-hidden="true" />
            <h2>Analysis status</h2>
          </div>
          <div className="PROTEORISK-docs-status-list">
            {[
              ['pending_upload', 'The workbook upload has not finished yet.'],
              ['queued', 'Your analysis is waiting for a free worker.'],
              ['running', 'Your protein pattern is being scored.'],
              ['completed', 'Your results are ready to open.'],
              ['failed', 'The workbook could not be scored. The analysis page says why.'],
            ].map(([status, text]) => (
              <div key={status} className="PROTEORISK-docs-status-item">
                <StatusBadge status={status} />
                <span>{text}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="PROTEORISK-docs-section">
          <div className="PROTEORISK-docs-section-header">
            <i className="fas fa-question-circle" aria-hidden="true" />
            <h2>Frequently asked questions</h2>
          </div>
          <div className="PROTEORISK-faq-list">
            {FAQ.map((item) => (
              <div key={item.q} className="PROTEORISK-faq-item">
                <h4><i className="fas fa-chevron-right" aria-hidden="true" /> {item.q}</h4>
                <p>{item.a}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="PROTEORISK-docs-section PROTEORISK-docs-support">
          <div className="PROTEORISK-docs-section-header">
            <i className="fas fa-life-ring" aria-hidden="true" />
            <h2>Need help?</h2>
          </div>
          <p className="PROTEORISK-docs-text">
            For questions, feedback or technical support, write to our team. For programmatic access, see the
            {' '}<Link to="/proteorisk/api">API page</Link>.
          </p>
          <a href="mailto:hello@phenomelongevity.com" className="PROTEORISK-docs-email-link">
            <i className="fas fa-envelope" aria-hidden="true" /> hello@phenomelongevity.com
          </a>
        </div>
      </div>
    </div>
  );
}
